import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Dispatch } from 'redux'
import { isBefore, isSameDay, parseISO, startOfDay, subDays } from 'date-fns'
import { isEmpty } from 'lodash'

import { usePositionById, useTags } from 'Api'
import { DateTime, HideOverflow } from 'components/UI'
import { Card, RouterLink, Dropdown } from 'ui'
import { extractTranslation, getProjectCode, hasAccessToThis } from 'services/Utilities'
import { canEmployeePunch } from 'services/PunchCards'
import { openModal } from 'store/actions/modals'
import PunchInformation from './PunchInformation'
import PunchDropdownOptions from './PunchDropdownOptions'
import ShiftTags from './ShiftTags'
import ShiftBadges from './ShiftBadges'
import AttachmentsList from 'Employee/schedule/AttachmentsList'
import { AttendanceInfo } from 'Employee/schedule/Attendance'
import { TimeOffInfo } from 'Employee/schedule/TimeOff'

export type ShiftType = MData.Event & {
  hasAbsence?: boolean
  hasTimeOff?: boolean
  hasEarlyOut?: boolean
  hasLateArrival?: boolean
  linkedAttendances?: MData.Attendance[]
  canCreateAttendance?: boolean
  vacationTypeId?: string
  shiftTimeOffId?: string
  punchCard?: MData.PunchCard | 'notLoaded' | 'noShift' | 'errorLoading'
}

const Shift: FC<{ shift: ShiftType; welcomeBanner?: boolean }> = ({ shift, welcomeBanner = false }) => {
  const {
    _id,
    tags,
    hasAbsence = false,
    hasEarlyOut = false,
    hasLateArrival = false,
    hasTimeOff = false,
    positionId,
    projects = [],
    punchCard,
    start,
    linkedAttendances = [],
    vacationTypeId = '',
    shiftTimeOffId = '',
    isOnCall = false,
    comment = '',
    files = []
  } = shift

  const dispatch = useDispatch()
  const { data: position } = usePositionById(positionId)
  const { data: tagsData } = useTags()
  const tagsById = tagsData?.tagsById || {}

  const isToday = isSameDay(new Date(), parseISO(start))
  const isBeforeOrToday = isBefore(startOfDay(parseISO(start)), new Date())
  const isBeforeToday = isBefore(startOfDay(parseISO(start)), subDays(new Date(), 1))
  const tagsVisibleInSchedule = tags?.filter(tagId => tagsById[tagId]?.showInEmployeeSchedule).map(tagId => tagsById[tagId])
  const shiftHasStarted = isBefore(parseISO(start), new Date())

  // Permissions
  const permissionToSeeProject = hasAccessToThis('projectSee')
  const permissionToSeePunchInfo = hasAccessToThis('seeOwnPunches')
  const permissionToPunch = hasAccessToThis('punchOnShifts')
  const permissionToCreateAttendance = hasAccessToThis('attendanceCreate')
  const permissionToSeeCoworkersSchedule = hasAccessToThis('scheduleSeeCoworkers')

  // Check if it is 12h before/after start or end of shift
  const { allowedToPunchOut } = canEmployeePunch(shift)

  //Styles
  const shiftOpacity = isBeforeToday && !allowedToPunchOut ? 0.6 : 1
  const requestBorderColor = hasAbsence ? ' border-danger' : hasTimeOff ? ' border-complimentary' : hasEarlyOut || hasLateArrival ? ' border-warning' : ''
  const shiftClassName = `mb-1 ${isToday ? ' border-primary' : ''}${requestBorderColor}`

  return (
    <Card id={`my_schedule_shift_${_id}`} className={shiftClassName} key={shift._id} style={{ opacity: shiftOpacity, color: welcomeBanner ? '#343a40' : '' }}>
      <Card.Body className="p-2 position-relative">
        <div className="d-flex flex-column">
          <ShiftBadges
            _id={_id}
            hasTimeOff={hasTimeOff}
            punchCard={punchCard}
            linkedAttendances={linkedAttendances}
            shiftTimeOffId={shiftTimeOffId}
            isOnCall={isOnCall}
            shiftHasStarted={shiftHasStarted}
            permission={!!permissionToSeePunchInfo || !!permissionToPunch}
          />
          <ShiftDateTime shift={shift} welcomeBanner={welcomeBanner} />
          <ShiftDropDown
            shift={shift}
            dispatch={dispatch}
            permissionToPunch={!!permissionToPunch}
            permissionToCreateAttendance={!!permissionToCreateAttendance}
            permissionToSeeCoworkersSchedule={!!permissionToSeeCoworkersSchedule}
          />
          {hasTimeOff ? <TimeOffInfo _id={shiftTimeOffId} vacationTypeId={vacationTypeId} /> : null}
          {linkedAttendances.map(attendance => (
            <AttendanceInfo key={`event_${_id}_attendance_${attendance._id}`} attendance={attendance} />
          ))}
          <ShiftPosition name={position?.name} color={position?.color} />
          <ShiftProject projects={projects} dispatch={dispatch} permission={!!permissionToSeeProject} />
          <ShiftTags tags={tagsVisibleInSchedule} />
          <ShiftComment comment={comment} welcomeBanner={welcomeBanner} />
          <ShiftPunchInformation punchCard={punchCard} isBeforeOrToday={isBeforeOrToday} permission={!!permissionToSeePunchInfo || !!permissionToPunch} />
          <AttachmentsList files={files} />
        </div>
      </Card.Body>
    </Card>
  )
}

const ShiftDateTime: FC<{ shift: ShiftType; welcomeBanner: boolean }> = ({ shift, welcomeBanner }) => {
  const { start, end, isExtraShift } = shift
  return (
    <>
      {welcomeBanner ? (
        <div data-cy="welcomeBanner_date" className="mb-1">
          <DateTime date={start} outFormat="humanDateFull" />
        </div>
      ) : null}
      <h5 className="m-0" data-cy="shift_time">
        <DateTime date={[start, end]} outFormat="time" range noParantheses isExtraShift={!!isExtraShift} />
      </h5>
    </>
  )
}

const ShiftPosition: FC<{ name?: string; color?: string }> = ({ name, color }) => {
  return (
    <div className="my-2">
      <i className="fa-solid fa-map-pin mr-2" style={{ color: color }} data-cy="icon_position"></i>
      {name}
    </div>
  )
}

const ShiftProject: FC<{ projects: MData.Event['projects']; dispatch: Dispatch; permission: boolean }> = ({ projects, dispatch, permission }) => {
  if (!permission || !projects || isEmpty(projects)) return null

  return (
    <div className="mb-2">
      <i className="fad fa-book mr-2"></i>
      <span
        data-cy="project"
        className="text-link"
        onClick={() => dispatch(openModal('EmployeeScheduleProjectInformationModal', { projectId: projects?.[0] }))}
      >
        [{getProjectCode(projects?.[0])}]
      </span>
    </div>
  )
}

const ShiftComment: FC<{ comment: string; welcomeBanner: boolean }> = ({ comment, welcomeBanner }) => {
  const { t } = useTranslation()
  const translatedComment = extractTranslation(comment, { extractCurrentLanguage: true }) as string
  if (!comment) return null
  if (welcomeBanner)
    return (
      <RouterLink to="/my/schedule" style={{ color: '#343a40' }}>
        <i className="fas fa-comment mr-2" />
        {t('employee.mySchedule.VIEW_COMMENT')}
      </RouterLink>
    )

  return (
    <div data-cy="comment">
      <small className="text-muted">{t('employee.mySchedule.ADDITIONAL_INFORMATION')}</small>
      <HideOverflow parsedValues={translatedComment.split('')} hideOverflow={100} delimiter={''} isComment />
    </div>
  )
}

type ShiftPunchInformationType = {
  punchCard?: MData.PunchCard | 'notLoaded' | 'noShift' | 'errorLoading'
  isBeforeOrToday: boolean
  permission: boolean
}

const ShiftPunchInformation: FC<ShiftPunchInformationType> = ({ punchCard, isBeforeOrToday, permission }) => {
  if (!punchCard || typeof punchCard !== 'object' || !permission) return null
  return (
    <div className="mt-1">
      <PunchInformation punchCard={punchCard} isBeforeOrToday={isBeforeOrToday} />
    </div>
  )
}

type ShiftDropDownType = {
  shift: ShiftType
  dispatch: Dispatch
  permissionToPunch: boolean
  permissionToCreateAttendance: boolean
  permissionToSeeCoworkersSchedule: boolean
}

const ShiftDropDown: FC<ShiftDropDownType> = ({ shift, dispatch, permissionToPunch, permissionToCreateAttendance, permissionToSeeCoworkersSchedule }) => {
  const { t } = useTranslation()
  const { _id, punchCard, start, end, canCreateAttendance } = shift
  const { allowedToPunchOut, allowedToPunchIn } = canEmployeePunch(shift)

  const hasPunchOptions =
    permissionToPunch || !(typeof punchCard === 'object' && punchCard?.leave) || (typeof punchCard !== 'object' && punchCard !== 'notLoaded')

  if (!allowedToPunchOut && !allowedToPunchIn) return null
  return (
    <>
      <Dropdown.Toggle size="small" data-cy="shift-quick-actions" outlined color="secondary" className="position-absolute right-0 top-0 m-2" />
      <Dropdown.Menu>
        <Dropdown.Header>{t('employee.mySchedule.SHIFT_OPTIONS')}</Dropdown.Header>
        {permissionToCreateAttendance && canCreateAttendance ? (
          <Dropdown.Link to={`/my/attendance/new?eventId=${_id}`} data-cy="create-attendance-request">
            <i className="fas fa-unlink fa-fw mr-2"></i>
            {t('employee.mySchedule.CREATE_ATTENDANCE_REQUEST')}
          </Dropdown.Link>
        ) : null}
        {permissionToSeeCoworkersSchedule ? (
          <Dropdown.Item onClick={() => dispatch(openModal('CoworkerModal', { rangeStart: start, rangeEnd: end }))}>
            <i className="fas fa-calendar-alt fa-fw mr-2"></i>
            {t('employee.mySchedule.SEE_MY_CO_WORKERS')}
          </Dropdown.Item>
        ) : null}
        {hasPunchOptions ? <PunchDropdownOptions event={shift} punchCard={punchCard} /> : null}
      </Dropdown.Menu>
    </>
  )
}

export { Shift }
